import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { takeUntil } from 'rxjs';

import {
  IrisButtonModule,
  IrisButtonTypes
} from '@sondermind/iris/button';
import {
  DropdownOption, NavigationKeys
} from '@sondermind/iris/dropdown';
import { SharedFormPipesModule } from '@sondermind/iris/form-helpers';
import {
  IrisIconButtonModule,
  IrisIconButtonTypes,
} from '@sondermind/iris/icon-button';
import {
  IconColors,
  IconSizes,
  IrisIconModule,
} from '@sondermind/iris/icon';
import { IrisModalRef } from '@sondermind/iris/modal';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';

import {
  CASH_PAY_OPTION_VALUE,
  IInsuranceOptionValue,
  InsuranceCategoryName,
  InsuranceType,
  NoInsuranceOptionValue
} from '@sondermind/utilities/models-provider-network-company';

import { IMoreFiltersConfig, IMoreFiltersSelections } from '../../models/more-filters.interface';
import { IOptionCategory } from '../../models/option-category.interface';
import { FilterOptionsStore } from '../../stores/filter-options.store';

@Component({
  selector: 'more-filters-modal',
  standalone: true,
  imports: [
    CommonModule,
    IrisButtonModule,
    IrisIconModule,
    IrisIconButtonModule,
    SharedFormPipesModule
  ],
  templateUrl: './more-filters-modal.component.html',
  styleUrls: ['./more-filters-modal.component.scss']
})
export class MoreFiltersModalComponent implements OnInit {
  cachedInitialSelections!: IMoreFiltersSelections;

  insuranceControl = new FormControl<IInsuranceOptionValue | null>(null);
  genderControl = new FormControl<number[]>([], { nonNullable: true });
  locationControl = new FormControl<string | null>(null);

  genderOptions: Array<DropdownOption<number>> = [];
  locationOptions: Array<DropdownOption<string>> = [];

  // insurance management
  noInsuranceOptions: Array<DropdownOption<IInsuranceOptionValue>> = [{
    name: 'I\'m not sure',
    value: {
      pncId: NoInsuranceOptionValue.NOT_SURE,
      title: 'not sure',
      type: InsuranceType.CASH,
      tag: ''
    }
  }, {
    name: 'I\'ll pay out of pocket',
    value: CASH_PAY_OPTION_VALUE
  }];
  private allInsuranceCategorizedOptions: Array<IOptionCategory<IInsuranceOptionValue>> = [{
    name: '',
    options: this.noInsuranceOptions
  }];
  insuranceCategorizedOptionsToDisplay: Array<IOptionCategory<IInsuranceOptionValue>> = [{
    name: '',
    options: this.noInsuranceOptions
  }];

  isLoadingNewInsuranceOptions: boolean = false;
  isInsuranceMenuOpen: boolean = false;
  isAnyEapOptions: boolean = false;
  isAnyMedicareOptions: boolean = false;
  isAnyHealthInsuranceOptions: boolean = false;
  isHealthInsuranceOptionSelected: boolean = false;
  isEapOptionSelected: boolean = false;
  isMedicareOptionSelected: boolean = false;

  currentFocusedValue: string | null = null;
  currentHoveredValue: string | null = null;

  // button config values
  primaryIrisButtonType = IrisButtonTypes.PRIMARY;
  tertiaryIrisButtonType = IrisButtonTypes.TERTIARY;
  tertiaryIrisIconButtonType = IrisIconButtonTypes.TERTIARY;
  resetButtonType = IrisButtonTypeAttributeOptions.RESET;
  submitButtonType = IrisButtonTypeAttributeOptions.SUBMIT;

  // icon config values
  iconSize = IconSizes.MD;
  iconColor = IconColors.BRAND1;

  destroyed$ = new EventEmitter<void>();

  constructor(
    private irisModalRef: IrisModalRef<MoreFiltersModalComponent, IMoreFiltersConfig, IMoreFiltersSelections | null>,
    private filtersStore: FilterOptionsStore,
  ) {}

  ngOnInit(): void {
    const configData = this.irisModalRef.config.data || this.emptyConfig();

    this.cachedInitialSelections = {
      genderSelections: configData.genderSelect.currentSelections,
      insuranceSelection: configData.insuranceSelect.currentSelection,
      locationSelection: configData.locationSelect.currentSelection
    };

    this.initializeGender(configData);
    this.initializeInsurance(configData);
    this.initializeLocation(configData);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private initializeGender(config: IMoreFiltersConfig): void {
    this.genderControl.setValue(config.genderSelect.currentSelections);
    this.genderOptions = config.genderSelect.options.sort(this.sortDropdownOptionsByName);
  }

  private initializeInsurance(config: IMoreFiltersConfig): void {
    this.filtersStore.insuranceList$().pipe(
      takeUntil(this.destroyed$),
    ).subscribe((insurances) => {
      const medicareCategory = this.emptyInsuranceCategory(InsuranceCategoryName.MEDICARE);
      const eapCategory = this.emptyInsuranceCategory(InsuranceCategoryName.EAP);
      const healthInsuranceCategory = this.emptyInsuranceCategory(InsuranceCategoryName.INSURANCE);

      insurances.forEach((insurance) => {
        if (insurance.isMedicare) {
          medicareCategory.options.push(
            {
              name: insurance.title,
              value: {
                pncId: insurance.pncId,
                title: insurance.title,
                type: InsuranceType.MEDICARE,
                tag: insurance.tag
              }
            });
        } else if (insurance.isEap) {
          eapCategory.options.push(
            {
              name: insurance.title,
              value: {
                pncId: insurance.pncId,
                title: insurance.title,
                type: InsuranceType.EAP,
                tag: insurance.tag
              }
            });
        } else {
          healthInsuranceCategory.options.push({
            name: insurance.title,
            value: {
              pncId: insurance.pncId,
              title: insurance.title,
              type: InsuranceType.INSURANCE,
              tag: insurance.tag
            }
          });
        }
      });

      this.allInsuranceCategorizedOptions.push(
        medicareCategory,
        healthInsuranceCategory,
        eapCategory
      );

      this.isAnyMedicareOptions = medicareCategory.options.length > 0;
      this.isAnyEapOptions = eapCategory.options.length > 0;
      this.isAnyHealthInsuranceOptions = healthInsuranceCategory.options.length > 0;

      this.setInsuranceControlValue(config);
      this.displayInsuranceSelection();

      this.isLoadingNewInsuranceOptions = false;
    });
  }

  private setInsuranceControlValue(config: IMoreFiltersConfig): void {
    const selection = config.insuranceSelect.currentSelection;

    if (selection) {
      /* eslint-disable @typescript-eslint/prefer-for-of */
      for (let i = 0; i < this.allInsuranceCategorizedOptions.length; i += 1) {
        const { options } = this.allInsuranceCategorizedOptions[i];

        const selectedOption = options.find(
          (option) => option.value.pncId === selection.pncId && option.value.type === selection.type
        );

        if (selectedOption) {
          this.insuranceControl.setValue(selectedOption.value);
          return;
        }
      }
      /* eslint-enable @typescript-eslint/prefer-for-of */
    }
  }

  private initializeLocation(config: IMoreFiltersConfig): void {
    this.locationControl.setValue(config.locationSelect.currentSelection);
    this.locationOptions = config.locationSelect.options.sort(this.sortDropdownOptionsByName);
  }

  private sortDropdownOptionsByName<T>(option1: DropdownOption<T>, option2: DropdownOption<T>): number {
    return option1.name.localeCompare(option2.name);
  }

  /** We only show sub-menu titles on the main modal menu.
   * If we have an selection, highlight the sub menu of that type */
  private displayInsuranceSelection(): void {
    const selectedValue = this.insuranceControl.value;

    this.setSelectedInsuranceType(selectedValue);
  }

  /** Undoes any changes that were made to the filter when it was open */
  cancel(): void {
    this.irisModalRef.close(null);
  }

  /** Closes filter with the updated selections */
  confirm(): void {
    this.irisModalRef.close({
      genderSelections: this.genderControl.value,
      insuranceSelection: this.insuranceControl.value,
      locationSelection: this.locationControl.value
    });
  }

  handleGenderKeyDown(e: KeyboardEvent, value: number): void {
    if (e.key === NavigationKeys.Enter || e.key === NavigationKeys[' ']) {
      this.updateGenderValue(value);
    }
  }

  handleInsuranceKeyDown(e: KeyboardEvent, value: IInsuranceOptionValue): void {
    if (e.key === NavigationKeys.Enter || e.key === NavigationKeys[' ']) {
      const newValue = this.insuranceControl.value === value ? null : value;
      this.updateInsuranceValue(newValue);
    }
  }

  handleLocationKeyDown(e: KeyboardEvent, value: string): void {
    if (e.key === NavigationKeys.Enter || e.key === NavigationKeys[' ']) {
      this.updateLocationValue(value);
    }
  }

  onHoverEnter(value: string): void {
    this.currentHoveredValue = value;
  }

  onHoverLeave(): void {
    this.currentHoveredValue = null;
  }

  resetValues(insuranceOnly: boolean): void {
    if (insuranceOnly) {
      this.insuranceControl.reset();
    } else {
      this.genderControl.reset();
      this.insuranceControl.reset();
      this.setSelectedInsuranceType(null);

      // Location should never be voided - reset to initial value
      this.locationControl.setValue(this.cachedInitialSelections.locationSelection ?? null);
    }
  }

  returnToMoreFilters(): void {
    this.isInsuranceMenuOpen = false;
    this.displayInsuranceSelection();
  }

  setSelectedInsuranceType(insuranceValue: IInsuranceOptionValue | null): void {
    this.isHealthInsuranceOptionSelected = false;
    this.isMedicareOptionSelected = false;
    this.isEapOptionSelected = false;

    if (!insuranceValue) {
      return;
    }

    if (insuranceValue.type === InsuranceType.MEDICARE) {
      this.isMedicareOptionSelected = true;
    }

    if (insuranceValue.type === InsuranceType.EAP) {
      this.isEapOptionSelected = true;
    }

    if (insuranceValue.type === InsuranceType.INSURANCE) {
      this.isHealthInsuranceOptionSelected = true;
    }
  }

  showFullInsuranceMenu(): void {
    this.isInsuranceMenuOpen = true;
    this.insuranceCategorizedOptionsToDisplay = this.allInsuranceCategorizedOptions;
  }

  showHealthInsuranceSubMenu(): void {
    this.isInsuranceMenuOpen = true;
    const healthInsuranceOptions = this.allInsuranceCategorizedOptions.filter(
      (category) => category.name === InsuranceCategoryName.INSURANCE
    );
    this.insuranceCategorizedOptionsToDisplay = healthInsuranceOptions || [];
  }

  showEapSubMenu(): void {
    this.isInsuranceMenuOpen = true;
    const eapOptions = this.allInsuranceCategorizedOptions.filter(
      (category) => category.name === InsuranceCategoryName.EAP
    );
    this.insuranceCategorizedOptionsToDisplay = eapOptions || [];
  }

  showMedicareSubMenu(): void {
    this.isInsuranceMenuOpen = true;
    const medicareOptions = this.allInsuranceCategorizedOptions.filter(
      (category) => category.name === InsuranceCategoryName.MEDICARE
    );
    this.insuranceCategorizedOptionsToDisplay = medicareOptions || [];
  }

  updateGenderValue(newValue: number): void {
    if (!this.genderControl.value.includes(newValue)) {
      this.genderControl.setValue([...this.genderControl.value, newValue]);
    } else {
      const newSelections = this.genderControl.value.filter((val) => val !== newValue);
      this.genderControl.setValue(newSelections);
    }
    this.genderControl.markAsTouched();
    this.genderControl.markAsDirty();
  }

  updateInsuranceValue(value: IInsuranceOptionValue | null): void {
    this.insuranceControl.setValue(value);
    this.insuranceControl.markAsTouched();
    this.insuranceControl.markAsDirty();
    this.setSelectedInsuranceType(value);
  }

  updateLocationValue(value: string): void {
    this.locationControl.setValue(value);
    this.locationControl.markAsTouched();
    this.locationControl.markAsDirty();
    this.filtersStore.setCurrentState(value);
    // We need to load new insurances when state changes
    this.isLoadingNewInsuranceOptions = true;
  }

  get isAnyValueSelected(): boolean {
    // Location can never be empty so it is not taken into account here.
    return !!this.insuranceControl.value || this.genderControl.value.length > 0;
  }

  /** An "empty" config object, to appease the strict typing gods */
  private emptyConfig(): IMoreFiltersConfig {
    return {
      insuranceSelect: {
        currentSelection: null,
        categorizedOptions: []
      },
      genderSelect: { currentSelections: [], options: [] },
      locationSelect: { currentSelection: null, options: [] }
    };
  }

  /** An "empty" config object, to appease the strict typing gods */
  private emptyInsuranceCategory(name: string): IOptionCategory<IInsuranceOptionValue> {
    return {
      name,
      options: []
    };
  }
}
